import { IRecipient } from '@nkr/models/index';
import { IHlaData } from '@nkr/models/hla-data';
import { Panel } from 'react-bootstrap';
import { HlaTableWrapper, PanelWrapper } from './styles';

/**
 * Props are the structural subset this component reads, not the full donor and compatibility
 * contracts. Both IPotentialDonor / ICompatibilityResult and the slimmer pre-select types satisfy
 * them, so the recipient pre-select grid can pass its own shapes without changing anything here
 * or for the other views that render this component.
 */
interface IProps {
    donor: { hlaData: IHlaData },
    recipient: IRecipient,
    compatibilityResult: {
        hlaPointsA?: number,
        hlaPointsB?: number,
        hlaPointsDr?: number,
        totalHlaPoints?: number
    },
    showAvoidsList: boolean,
    showSmaller?: boolean
}

const HlaMatches = (props: IProps) => {
    const avoidsCol = (avoids: number[], elementType: string) => {
        if (props.showAvoidsList) {
            return avoids.length == 0 ? <td>&nbsp;</td> : <td><span title={avoids.join('|')}>{avoids.length}</span></td>;
        }

        return null;
    };

    const avoidsBool = (avoid: boolean) => {
        if (props.showAvoidsList) {
            return avoid == true ? <td><span className='glyphicon glyphicon-remove'></span></td> : <td>&nbsp;</td>;
        }

        return null;
    };

    const getHlaRow = (donorHla: IHlaData, recipientHla: IHlaData, hla1: string, hla2: string, avoids: number[]) => {
        const donorHla1Value = donorHla['hla' + hla1];
        const donorHla1HighResValue = donorHla['hla' + hla1 + 'HighRes'];
        const donorHla2Value = donorHla['hla' + hla2];
        const donorHla2HighResValue = donorHla['hla' + hla2 + 'HighRes'];
        const recipientHla1Value = recipientHla['hla' + hla1];
        const recipientHla1HighResValue = recipientHla['hla' + hla1 + 'HighRes'];
        const recipientHla2Value = recipientHla['hla' + hla2];
        const recipientHla2HighResValue = recipientHla['hla' + hla2 + 'HighRes'];

        var avoidsList = avoids != null ? avoids : [];

        let label1 = '';
        let label2 = '';

        if (avoidsList.indexOf(donorHla1Value) >= 0) {
            label1 = 'label-danger';
        } else {
            if (donorHla1Value == recipientHla1Value || donorHla1Value == recipientHla2Value) {
                label1 = 'label-success';
            } else {
                label1 = 'label-warning';
            }
        }

        if (avoidsList.indexOf(donorHla2Value) >= 0) {
            label2 = 'label-danger';
        } else {
            if (donorHla2Value == recipientHla1Value || donorHla2Value == recipientHla2Value) {
                label2 = 'label-success';
            } else {
                label2 = 'label-warning';
            }
        }

        return (
            <>
                <td style={{ backgroundColor: '#EEE' }}><span className={'label ' + label1}>{donorHla1Value}</span></td>
                <td style={{ backgroundColor: '#EEE' }}><p className='hla-high-res'>{donorHla1HighResValue}</p></td>
                <td style={{ backgroundColor: '#EEE' }}><span className={'label ' + label2}>{donorHla2Value}</span></td>
                <td style={{ backgroundColor: '#EEE' }}>{donorHla2HighResValue}</td>
                <td><span className='label label-default'>{recipientHla1Value}</span>{recipientHla1HighResValue}</td>
                <td><span className='label label-default'>{recipientHla2Value}</span>{recipientHla2HighResValue}</td>
            </>
        )
    };

    const getHlaBoolRow = (donorHasAntigen, donorHighRes1, donorHighRes2, recipHasAntigen, recipIsAvoided) => {
        let label1 = recipIsAvoided && donorHasAntigen ? 'label-danger' : (donorHasAntigen && !recipHasAntigen ? 'label-warning' : 'label-success');
        let labelText = donorHasAntigen ? 'yes' : 'no';
        let labelRecipText = recipHasAntigen ? 'yes' : 'no';

        return (
            <>
                <td style={{ backgroundColor: '#EEE' }}><span className={'label ' + label1}>{labelText}</span></td>
                <td style={{ backgroundColor: '#EEE' }}><p className='hla-high-res'>{donorHighRes1}</p></td>
                <td style={{ backgroundColor: '#EEE' }}>&nbsp;</td>
                <td style={{ backgroundColor: '#EEE' }}>{donorHighRes2}</td>
                <td><span className='label label-default'>{labelRecipText}</span></td>
                <td>&nbsp;</td>
            </>
        );
    };

    const { compatibilityResult, donor, recipient } = props;

    const aAvoids = recipient.hlaAvoids.aAvoids != null ? recipient.hlaAvoids.aAvoids : [];
    const bAvoids = recipient.hlaAvoids.bAvoids != null ? recipient.hlaAvoids.bAvoids : [];
    const drAvoids = recipient.hlaAvoids.drAvoids != null ? recipient.hlaAvoids.drAvoids : [];
    const dqAvoids = recipient.hlaAvoids.dqAvoids != null ? recipient.hlaAvoids.dqAvoids : [];
    const bwAvoids = recipient.hlaAvoids.bwAvoids != null ? recipient.hlaAvoids.bwAvoids : [];
    const cwAvoids = recipient.hlaAvoids.cwAvoids != null ? recipient.hlaAvoids.cwAvoids : [];
    const dpAvoids = recipient.hlaAvoids.dpAvoids != null ? recipient.hlaAvoids.dpAvoids : [];
    const dqaAvoids = recipient.hlaAvoids.dqaAvoids != null ? recipient.hlaAvoids.dqaAvoids : [];

    return (
        <PanelWrapper small={props.showSmaller}>
            <Panel.Heading style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                <Panel.Title><span className='heading medium'>HLA Information</span></Panel.Title>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <div style={{ marginRight: 15 }}><span className='label label-success' style={{ marginBottom: 0 }}>#</span> not a mismatch</div>
                    <div style={{ marginRight: 15 }}><span className='label label-warning' style={{ marginBottom: 0 }}>#</span> mismatch</div>
                    <div><span className='label label-danger' style={{ marginBottom: 0 }}>#</span> avoid conflict</div>
                </div>
            </Panel.Heading>
            <HlaTableWrapper className='table table-condensed table-striped table-nkr' small={props.showSmaller}>
                <thead>
                    <tr>
                        <th>HLA</th>
                        <th style={{ backgroundColor: '#EEE' }} colSpan={4}>Donor</th>
                        <th colSpan={2}>Recip</th>
                        <th style={{ backgroundColor: '#EEE' }}>HLA Pts</th>
                        {props.showAvoidsList && (
                            <th>Avoids *</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>A</td>
                        {getHlaRow(donor.hlaData, recipient.hlaData, 'A1', 'A2', recipient.hlaAvoids.aAvoids)}
                        <td>{compatibilityResult.hlaPointsA}</td>
                        {avoidsCol(aAvoids, 'td')}
                    </tr>
                    <tr>
                        <td>B</td>
                        {getHlaRow(donor.hlaData, recipient.hlaData, 'B1', 'B2', recipient.hlaAvoids.bAvoids)}
                        <td>{props.compatibilityResult.hlaPointsB}</td>
                        {avoidsCol(bAvoids, 'td')}
                    </tr>
                    <tr>
                        <td>DR</td>
                        {getHlaRow(donor.hlaData, recipient.hlaData, 'Dr1', 'Dr2', recipient.hlaAvoids.drAvoids)}
                        <td>{props.compatibilityResult.hlaPointsDr}</td>
                        {avoidsCol(drAvoids, 'td')}
                    </tr>
                    <tr>
                        <td>DQ</td>
                        {getHlaRow(donor.hlaData, recipient.hlaData, 'Dq1', 'Dq2', recipient.hlaAvoids.dqAvoids)}
                        <td>--</td>
                        {avoidsCol(dqAvoids, 'td')}
                    </tr>
                    <tr>
                        <td>BW</td>
                        {getHlaRow(donor.hlaData, recipient.hlaData, 'Bw1', 'Bw2', recipient.hlaAvoids.bwAvoids)}
                        <td>--</td>
                        {avoidsCol(bwAvoids, 'td')}
                    </tr>
                    <tr>
                        <td>CW</td>
                        {getHlaRow(donor.hlaData, recipient.hlaData, 'Cw1', 'Cw2', recipient.hlaAvoids.cwAvoids)}
                        <td>--</td>
                        {avoidsCol(cwAvoids, 'td')}
                    </tr>
                    <tr>
                        <td>DP</td>
                        {getHlaRow(donor.hlaData, recipient.hlaData, 'Dp1', 'Dp2', recipient.hlaAvoids.dpAvoids)}
                        <td>--</td>
                        {avoidsCol(dpAvoids, 'td')}
                    </tr>
                    <tr>
                        <td>DQa</td>
                        {getHlaRow(donor.hlaData, recipient.hlaData, 'Dqa1', 'Dqa2', recipient.hlaAvoids.dqaAvoids)}
                        <td>--</td>
                        {avoidsCol(dqaAvoids, 'td')}
                    </tr>
                    <tr>
                        <td>DR-51</td>
                        {getHlaBoolRow(donor.hlaData.hlaDr51, donor.hlaData.hlaDr51HighRes, donor.hlaData.hlaDr51HighRes2, recipient.hlaData.hlaDr51, recipient.hlaAvoids.avoidDr51)}
                        <td>--</td>
                        {avoidsBool(recipient.hlaAvoids.avoidDr51)}
                    </tr>
                    <tr>
                        <td>DR-52</td>
                        {getHlaBoolRow(donor.hlaData.hlaDr52, donor.hlaData.hlaDr52HighRes, donor.hlaData.hlaDr52HighRes2, recipient.hlaData.hlaDr52, recipient.hlaAvoids.avoidDr52)}
                        <td>--</td>
                        {avoidsBool(recipient.hlaAvoids.avoidDr52)}
                    </tr>
                    <tr>
                        <td>DR-53</td>
                        {getHlaBoolRow(donor.hlaData.hlaDr53, donor.hlaData.hlaDr53HighRes, donor.hlaData.hlaDr53HighRes2, recipient.hlaData.hlaDr53, recipient.hlaAvoids.avoidDr53)}
                        <td>--</td>
                        {avoidsBool(recipient.hlaAvoids.avoidDr53)}
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={9}>* Hover over to see the avoids</td>
                    </tr>
                </tfoot>
                <tfoot>
                    <tr style={{ backgroundColor: '#ddd' }}>
                        <td>TOTAL:</td>
                        <td colSpan={6}></td>
                        <td>{props.compatibilityResult.totalHlaPoints}</td>
                        <td></td>
                    </tr>
                </tfoot>

            </HlaTableWrapper>
        </PanelWrapper>
    );
}

export default HlaMatches;